import { createStore } from "vuex";
import axiosInstance from "@/api/axiosInstance";
import router from "@/router";

export default createStore({
  state: {
    token: localStorage.getItem("token") || null,
    isUserLoggedIn: !!localStorage.getItem("token"),
    user: null, // whole user object from backend
    loading: false,
    notifications: [],
    unreadNotificationCount: 0,
  },

  mutations: {
    setToken(state, token) {
      state.token = token;
      if (token) {
        localStorage.setItem("token", token);
        state.isUserLoggedIn = true;
      } else {
        localStorage.removeItem("token");
        state.isUserLoggedIn = false;
      }
    },
    setUser(state, user) {
      state.user = user;
    },
    clearAuth(state) {
      state.token = null;
      state.isUserLoggedIn = false;
      state.user = null;
      state.notifications = [];
      state.unreadNotificationCount = 0;
      localStorage.removeItem("token");
    },
    setLoading(state, payload) {
      state.loading = payload;
    },
    setNotifications(state, notifications) {
      state.notifications = notifications;
    },
    appendNotifications(state, notifications) {
      state.notifications.push(...notifications);
    },
    setUnreadCount(state, count) {
      state.unreadNotificationCount = count;
    },
    markNotificationRead(state, id) {
      const n = state.notifications.find((n) => n.id === id);
      if (n) n.read = true;
      if (state.unreadNotificationCount > 0) state.unreadNotificationCount--;
    },
    markAllNotificationsRead(state) {
      state.notifications.forEach((n) => (n.read = true));
      state.unreadNotificationCount = 0;
    },
  },

  actions: {
    async registerUser({ commit, dispatch }, { name, email, password }) {
      const res = await axiosInstance.post("api/user/create", {
        name,
        email,
        password,
      });

      // Assume backend returns token
      const { token } = res.data;
      commit("setToken", token);

      // Fetch user profile securely
      await dispatch("fetchUser");

      router.push("/dashboard");
    },

    async loginUser({ commit, dispatch }, { email, password }) {
      const res = await axiosInstance.post("/api/user/login", {
        email,
        password,
      });

      const { token } = res.data;
      commit("setToken", token);

      // Fetch user profile
      await dispatch("fetchUser");

      router.push("/dashboard");
    },

    async googleAuth({ commit, dispatch }) {
      const clientid = process.env.VUE_APP_GOOGLE_CLIENT_ID;

      try {
        // Load Google Identity Services script if not already loaded
        if (!window.google || !window.google.accounts) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://accounts.google.com/gsi/client";
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => reject("Failed to load Google script");
            document.head.appendChild(script);
          });
        }

        // Initialize OAuth2 Code Flow client
        const client = window.google.accounts.oauth2.initCodeClient({
          client_id: clientid,
          scope: "openid email profile",
          ux_mode: "popup",
          redirect_uri: "postmessage", // must match backend
          callback: async (response) => {
            const authCode = response.code; // OAuth2 authorization code

            try {
              commit("setLoading", true); // start loading
              // Send auth code to backend
              const res = await axiosInstance.post("/api/user/google/auth", {
                code: authCode,
              });

              // const data = await res.json();
              const data = res.data;

              if (data.status === "login") {
                commit("setToken", data.loginToken);
                await dispatch("fetchUser");
                commit("setLoading", false); // stop loading

                router.push("/dashboard");
              } else if (data.status === "signup") {
                commit("setToken", data.signupToken);
                await dispatch("fetchUser");
                commit("setLoading", false); // stop loading

                router.push("/dashboard");
              } else {
                alert("Unexpected response from server.");
              }
            } catch (err) {
              console.error("Error sending code to backend:", err);
            }
          },
        });

        // 👉 Trigger popup for user to select account
        client.requestCode();
      } catch (err) {
        console.error("Google Sign-In Error:", err);
      }
    },

    async editUser({ dispatch, state }, { name, email }) {
      if (!state.token) return;

      try {
        await axiosInstance.put(
          `/api/user/edit/${state.user.id}`,
          {
            name,
            email,
          },
          {
            headers: { Authorization: `Bearer ${state.token}` },
          }
        );

        await dispatch("fetchUser");
      } catch (err) {
        console.error("Failed to edit user", err);
      }
    },

    async fetchUser({ commit, state }) {
      if (!state.token) return;

      try {
        const res = await axiosInstance.get("/api/user/me", {
          headers: { Authorization: `Bearer ${state.token}` },
        });
        commit("setUser", res.data);
      } catch (err) {
        console.error("Failed to fetch user", err);
        commit("clearAuth");
      }
    },

    async logoutUser({ commit }) {
      commit("clearAuth");
    },

    // Generate report — credits are now deducted server-side atomically
    async generateReport({ dispatch, state }, { formData }) {
      let reportEndpoint;
      if (formData.reportType === "basic") {
        reportEndpoint = "/api/report/validate";
      } else if (formData.reportType === "advanced") {
        reportEndpoint = "/api/report/advanced-validate";
      }
      await axiosInstance.post(
        reportEndpoint,
        {
          idea: formData.idea,
          description: formData.description,
          reportType: formData.reportType,
        },
        { headers: { Authorization: `Bearer ${state.token}` } }
      );

      // Refresh user to get updated credits
      await dispatch("fetchUser");
    },

    async deleteReport({ state }, reportId) {
      await axiosInstance.delete(`/api/report/${reportId}`, {
        headers: { Authorization: `Bearer ${state.token}` },
      });
    },

    async forgotPassword(_, { email }) {
      const res = await axiosInstance.post("/api/user/forgot-password", { email });
      return res.data;
    },

    async resetPassword(_, { email, code, newPassword }) {
      const res = await axiosInstance.post("/api/user/reset-password", { email, code, newPassword });
      return res.data;
    },

    // --- Phase 3: Report Sharing & Idea Portfolio ---

    async generateShareLink({ state }, reportId) {
      const res = await axiosInstance.post(`/api/report/${reportId}/share`, {}, {
        headers: { Authorization: `Bearer ${state.token}` },
      });
      return res.data; // { shareToken, shareUrl }
    },

    async revokeShareLink({ state }, reportId) {
      await axiosInstance.delete(`/api/report/${reportId}/share`, {
        headers: { Authorization: `Bearer ${state.token}` },
      });
    },

    async updateReportStatus({ state }, { reportId, status }) {
      const res = await axiosInstance.patch(`/api/report/${reportId}/status`, { status }, {
        headers: { Authorization: `Bearer ${state.token}` },
      });
      return res.data;
    },

    async fetchPublicReport(_, shareToken) {
      const res = await axiosInstance.get(`/api/report/public/${shareToken}`);
      return res.data;
    },

    // --- Phase 4: Notifications & Preferences ---

    async fetchNotifications({ commit, state }, { page = 1, append = false } = {}) {
      if (!state.token) return;
      try {
        const res = await axiosInstance.get(`/api/notifications?page=${page}&limit=20`, {
          headers: { Authorization: `Bearer ${state.token}` },
        });
        if (append) {
          commit("appendNotifications", res.data.data);
        } else {
          commit("setNotifications", res.data.data);
        }
        return res.data.pagination;
      } catch (err) {
        console.error("Failed to fetch notifications", err);
      }
    },

    async fetchUnreadCount({ commit, state }) {
      if (!state.token) return;
      try {
        const res = await axiosInstance.get("/api/notifications/unread-count", {
          headers: { Authorization: `Bearer ${state.token}` },
        });
        commit("setUnreadCount", res.data.count);
      } catch (err) {
        console.error("Failed to fetch unread count", err);
      }
    },

    async markNotificationRead({ commit, state }, id) {
      if (!state.token) return;
      try {
        await axiosInstance.patch(`/api/notifications/${id}/read`, {}, {
          headers: { Authorization: `Bearer ${state.token}` },
        });
        commit("markNotificationRead", id);
      } catch (err) {
        console.error("Failed to mark notification as read", err);
      }
    },

    async markAllNotificationsRead({ commit, state }) {
      if (!state.token) return;
      try {
        await axiosInstance.patch("/api/notifications/read-all", {}, {
          headers: { Authorization: `Bearer ${state.token}` },
        });
        commit("markAllNotificationsRead");
      } catch (err) {
        console.error("Failed to mark all as read", err);
      }
    },

    async updatePreferences({ dispatch, state }, { savedCategories, newsletterOptOut }) {
      if (!state.token) return;
      try {
        const res = await axiosInstance.patch("/api/user/preferences", {
          savedCategories,
          newsletterOptOut,
        }, {
          headers: { Authorization: `Bearer ${state.token}` },
        });
        await dispatch("fetchUser");
        return res.data;
      } catch (err) {
        console.error("Failed to update preferences", err);
        throw err;
      }
    },
  },

  getters: {
    isLoggedIn: (state) => state.isUserLoggedIn,
    user: (state) => state.user,
    credits: (state) => (state.user ? state.user.credits : 0),
    unreadNotifications: (state) => state.unreadNotificationCount,
  },
});
